import { createContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Create a secure hash from workspace ID for URL (URL-safe base64, 16+ chars)
function hashWorkspaceId(id: string): string {
  // Use more of the ID for better security (24 chars of hex = 16 bytes)
  const cleaned = id.replace(/[^a-fA-F0-9]/g, '');
  // Use up to 24 hex chars (12 bytes = 16 base64 chars)
  const hex = cleaned.substring(0, 24).padEnd(24, '0');
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }
  // Create URL-safe base64 string (16 chars from 12 bytes)
  const base64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return base64; // Full 16 chars
}

// Find workspace by hash
function findWorkspaceByHash(workspaces: Workspace[], hash: string): Workspace | null {
  return workspaces.find(w => hashWorkspaceId(w.id) === hash) || null;
}

interface Workspace {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  created_at: string;
  created_by: string;
}

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  createWorkspace: (name: string, description?: string, color?: string, icon?: string) => Promise<Workspace>;
  updateWorkspace: (id: string, updates: Partial<Workspace>) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkspaces = async () => {
    // Don't fetch if no token
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/workspaces/');
      const data = response.data as { workspaces: Workspace[] };
      setWorkspaces(data.workspaces || []);
      
      // Check URL hash for workspace ID first, then localStorage
      const hashMatch = window.location.hash.match(/ws=([A-Za-z0-9_-]+)/);
      const urlHash = hashMatch ? hashMatch[1] : null;
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      
      // Find workspace by URL hash or saved ID
      const fromHash = urlHash ? findWorkspaceByHash(data.workspaces || [], urlHash) : null;
      const fromStorage = data.workspaces?.find((w: Workspace) => w.id === savedWorkspaceId);
      const selected = fromHash || fromStorage || data.workspaces?.[0];
      
      if (selected) {
        setCurrentWorkspaceState(selected);
        localStorage.setItem('currentWorkspaceId', selected.id);
        // Update hash if not present or different
        const selectedHash = hashWorkspaceId(selected.id);
        if (!urlHash || urlHash !== selectedHash) {
          window.location.hash = `ws=${selectedHash}`;
        }
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      setError('Impossibile caricare i workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const setCurrentWorkspace = (workspace: Workspace) => {
    // Save workspace ID to localStorage
    localStorage.setItem('currentWorkspaceId', workspace.id);
    // Update URL hash with new workspace
    const newHash = hashWorkspaceId(workspace.id);
    window.location.hash = `ws=${newHash}`;
    // Force complete page reload to refresh all data
    window.location.reload();
  };

  const createWorkspace = async (name: string, description?: string, color?: string, icon?: string): Promise<Workspace> => {
    const response = await api.post('/workspaces/', { name, description, color, icon });
    const data = response.data as { workspace: Workspace };
    await fetchWorkspaces();
    return data.workspace;
  };

  const updateWorkspace = async (id: string, updates: Partial<Workspace>) => {
    await api.put(`/workspaces/${id}`, updates);
    await fetchWorkspaces();
  };

  const deleteWorkspace = async (id: string) => {
    await api.delete(`/workspaces/${id}`);
    await fetchWorkspaces();
  };

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        loading,
        error,
        setCurrentWorkspace,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        refreshWorkspaces: fetchWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
